import type { Question } from '@/types';
import type { GamblingCompany, GamblingTip } from '@/components/success-gambling/context';
import {
  ANSWER_SEPARATOR,
  EMAILS,
  FIX_TIPS,
  KEY_TO_GET_COMPANIES,
  MAIL_BODY,
  MAIL_SUBJECT,
} from '@/components/success-gambling/utils/constants';

export const cleanText = (text: string): string => {
  return text
    .split('\n')
    .filter((line, index) => index > 0 || line.trim() !== '')
    .map((line) => line.replace(/^\t+/, ''))
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n');
};

export const getMailTo = (company: string): string => {
  return EMAILS[company] || '';
};

export const getHref = (companyName: string, fullName: string): string => {
  const mail = getMailTo(companyName);
  const bcc = 'kontakt@sportwetten-kanzlei.de';
  const body = encodeURIComponent(cleanText(MAIL_BODY.replace('{fullName}', fullName || 'Ihr Name')));
  const subject = MAIL_SUBJECT.replace('{fullName}', fullName || 'Ihr Name');
  return `mailto:${mail}?bcc=${bcc}&body=${body}&subject=${subject}`;
};

export const getNoNameHref = (fullName: string): string => {
  const bcc = 'kontakt@sportwetten-kanzlei.de';
  const body = encodeURIComponent(cleanText(MAIL_BODY.replace('{fullName}', fullName || 'Ihr Name')));
  const subject = MAIL_SUBJECT.replace('{fullName}', fullName || 'Ihr Name');
  return `mailto:?bcc=${bcc}&body=${body}&subject=${subject}`;
};

export const getMultiHref = (userEmail: string, companyNames: string[], fullName: string): string => {
  const bcc = ['kontakt@sportwetten-kanzlei.de', ...companyNames.map((companyName) => getMailTo(companyName))].join(
    ',',
  );
  const body = encodeURIComponent(cleanText(MAIL_BODY.replace('{fullName}', fullName || 'Ihr Name')));
  const subject = MAIL_SUBJECT.replace('{fullName}', fullName || 'Ihr Name');
  return `mailto:${userEmail}?bcc=${bcc}&body=${body}&subject=${subject}`;
};

export const getAllCompanyNames = (answers: Record<string, string>): string[] => {
  return [...new Set(KEY_TO_GET_COMPANIES.flatMap((key) => (answers[key] || '').split(ANSWER_SEPARATOR)))].filter(
    Boolean,
  );
};

export const getCompanyNames = (answers: Record<string, string>): string[] => {
  return getAllCompanyNames(answers)
    .filter(Boolean)
    .filter((c) => c !== 'Unsicher')
    .filter((c) => c !== 'Anderes Casino');
};

export const hasNoNameCompany = (answers: Record<string, string>): boolean => {
  return getAllCompanyNames(answers).includes('Anderes Casino');
};

export const getCompaniesMap = (allQuestions: Question[]): Record<string, string> => {
  return allQuestions.reduce(
    (companiesMap, { key, choices = [] }) => {
      if (KEY_TO_GET_COMPANIES.includes(key)) {
        choices.forEach(({ value, label = '' }) => (companiesMap[value] = label));
      }
      return companiesMap;
    },
    {} as Record<string, string>,
  );
};

export const convertCompanyNamesToCompany = (
  answers: Record<string, string>,
  fullName: string,
  email: string,
): GamblingCompany => {
  const companyNames = getCompanyNames(answers);

  if (companyNames.length < 1) {
    return {
      isNoNameCompanyCase: hasNoNameCompany(answers),
      href: getNoNameHref(fullName),
      label: 'DSA anfordern',
    };
  }

  return companyNames.length >= 2
    ? {
        isNoNameCompanyCase: hasNoNameCompany(answers),
        href: getMultiHref(email, companyNames, fullName),
        label: 'DSA anfordern',
      }
    : {
        isNoNameCompanyCase: hasNoNameCompany(answers),
        href: getHref(companyNames[0], fullName),
        label: `DSA von ${companyNames[0]} anfordern`,
      };
};

export const convertCompanyNamesToTips = (
  answers: Record<string, string>,
  allQuestions: Question[],
  fullName: string,
): GamblingTip[] => {
  const companiesMap = getCompaniesMap(allQuestions);

  return [
    ...getCompanyNames(answers).map(
      (company): GamblingTip => ({
        type: 'copy',
        twoLines: false,
        title: `An ${companiesMap[company] || ''}: `,
        text: getMailTo(cleanText(company)),
      }),
    ),
    ...FIX_TIPS,
  ].map((tip) => ({ ...tip, text: (tip.text || '').replace('{fullName}', fullName || 'Ihr Name') }));
};
